import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop, hexToRGBA } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import { saveNodeType, isHover } from "../utils";

import { getNewXY } from "./EachTrendLine";
import StraightLine from "../components/StraightLine";
import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import Text from "../components/Text";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

const DEFAULT_ARC_LEVELS = [
  { level: 38.2, display: true },
  { level: 50, display: true },
  { level: 61.8, display: true },
  { level: 100, display: true },
];

class EachFibArc extends Component {
  constructor(props) {
    super(props);

    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);

    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);

    this.handleHover = this.handleHover.bind(this);

    this.isHover = isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleLineDragStart() {
    const { x1, y1, x2, y2 } = this.props;

    this.dragStart = {
      x1,
      y1,
      x2,
      y2,
    };
  }

  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;

    const {
      x1: x1Value,
      y1: y1Value,
      x2: x2Value,
      y2: y2Value,
    } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(
      xScale,
      xAccessor,
      [x1 - dx, y1 - dy],
      fullData,
    );
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(
      xScale,
      xAccessor,
      [x2 - dx, y2 - dy],
      fullData,
    );
    const newY2Value = yScale.invert(y2 - dy);

    onDrag(index, {
      x1: newX1Value,
      y1: newY1Value,
      x2: newX2Value,
      y2: newY2Value,
    });
  }

  handlePoint1Drag(moreProps) {
    const [x1, y1] = getNewXY(moreProps);
    const { onPoint1Drag } = this.props;
    onPoint1Drag([x1, y1]);
  }

  handlePoint2Drag(moreProps) {
    const [x2, y2] = getNewXY(moreProps);
    const { onPoint2Drag } = this.props;
    onPoint2Drag([x2, y2]);
  }

  render() {
    const { x1, x2, y1, y2 } = this.props;
    const { interactive, appearance } = this.props;
    const yDisplayFormat = this.props.yDisplayFormat || ((v) => v.toFixed(2));

    // Don't render if missing points
    if (x1 === null || x2 === null) return null;

    // Extract values from registry format
    const stroke = appearance.stroke?.color || "#00BFFF";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray = appearance.stroke?.dash || "Solid";

    const fontFamily =
      appearance.font?.fontFamily ||
      "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = appearance.font?.fontSize || 11;
    const fontFill = appearance.font?.color || "#00BFFF";

    const fillColor = appearance.fill?.color || "#00BFFF";
    const fillOpacity =
      appearance.fill?.opacity !== undefined ? appearance.fill.opacity : 0.05;

    const { r } = appearance;

    // Get theme-aware edge colors
    const edgeColors = {
      stroke: "#000000",
      fill: "#FFFFFF",
    };

    const actualEdgeFill = edgeColors.fill;
    const circleRadius = r || 6;

    const { hoverText, selected } = this.props;
    const { hover } = this.state;
    const { onDragComplete } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    // Fibonacci levels - configurable via appearance.levels
    const configuredLevels = appearance.levels || DEFAULT_ARC_LEVELS;
    const activeLevels = configuredLevels.filter((l) => l.display !== false);
    const levels = activeLevels.map((l) => ({
      level: l.level / 100,
      label: `${l.level}%`,
    }));

    return (
      <g>
        {/* Render arcs using FibArcComponent */}
        <FibArcComponent
          ref={this.saveNodeType("arcs")}
          selected={selected || hover}
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={stroke}
          strokeWidth={hover || selected ? strokeWidth + 1 : strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeOpacity={strokeOpacity}
          fillColor={fillColor}
          fillOpacity={fillOpacity}
          fontFamily={fontFamily}
          fontSize={fontSize}
          fontFill={fontFill}
          levels={levels}
          yDisplayFormat={yDisplayFormat}
          interactiveCursorClass="react-stockcharts-move-cursor"
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineMove}
          onDragComplete={onDragComplete}
        />

        {/* Trend line (extended base line) */}
        <StraightLine
          ref={this.saveNodeType("trendline")}
          selected={false}
          type="XLINE"
          x1Value={x2}
          y1Value={y2}
          x2Value={2 * x1 - x2}
          y2Value={2 * y1 - y2}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
          interactiveCursorClass="react-stockcharts-move-cursor"
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineMove}
          onDragComplete={onDragComplete}
        />

        {/* Drag handles at anchor points */}
        {interactive && (hover || selected) && (
          <g>
            <ClickableCircle
              ref={this.saveNodeType("point1")}
              show={true}
              cx={x1}
              cy={y1}
              r={circleRadius}
              fill={actualEdgeFill}
              stroke={edgeColors.stroke}
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint1Drag}
              onDragComplete={onDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("point2")}
              show={true}
              cx={x2}
              cy={y2}
              r={circleRadius}
              fill={actualEdgeFill}
              stroke={edgeColors.stroke}
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint2Drag}
              onDragComplete={onDragComplete}
            />
          </g>
        )}

        {/* Hover tooltip */}
        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          {...restHoverTextProps}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

// FibArcComponent - Renders the actual arcs with labels
class FibArcComponent extends Component {
  constructor(props) {
    super(props);

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  isHover(moreProps) {
    const { tolerance, onHover } = this.props;
    const { mouseXY } = moreProps;
    const [mouseX, mouseY] = mouseXY;

    if (isDefined(onHover)) {
      const { arcs } = helper(this.props, moreProps);

      // Check bounding box containing all arcs
      const {
        xScale,
        chartConfig: { yScale },
      } = moreProps;
      const { x1, y1, x2, y2 } = this.props;

      const px1 = xScale(x1);
      const py1 = yScale(y1);
      const px2 = xScale(x2);
      const py2 = yScale(y2);

      // Calculate bounding area (from ChartIQ lines 12606-12626)
      const priceDistance = Math.abs(y2 - y1);
      const maxRadius = priceDistance * Math.sqrt(2);
      const maxRadiusPixels = Math.abs(yScale(y2 - maxRadius) - py2);

      // Extend bounding box by maxRadius
      const left = Math.min(px1, px2) - maxRadiusPixels;
      const right = Math.max(px1, px2) + maxRadiusPixels;
      const top = Math.min(py1, py2) - maxRadiusPixels;
      const bottom = Math.max(py1, py2) + maxRadiusPixels;

      // Simple bounding box check
      if (
        mouseX >= left - tolerance &&
        mouseX <= right + tolerance &&
        mouseY >= top - tolerance &&
        mouseY <= bottom + tolerance
      ) {
        return true;
      }
    }
    return false;
  }

  drawOnCanvas(ctx, moreProps) {
    const {
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
      fillColor,
      fillOpacity,
      fontFamily,
      fontSize,
      fontFill,
    } = this.props;

    const { arcs, trendLineEnd } = helper(this.props, moreProps);

    // Set styles
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = hexToRGBA(stroke, strokeOpacity);
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = fontFill;

    // Draw each arc
    arcs.forEach((arc) => {
      const {
        cx,
        cy,
        rx,
        ry,
        startAngle,
        endAngle,
        anticlockwise,
        label,
        labelX,
        labelY,
      } = arc;

      ctx.save();
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, startAngle, endAngle, anticlockwise);
      ctx.strokeStyle = hexToRGBA(stroke, strokeOpacity);
      ctx.stroke();

      // Fill arc with very low opacity
      ctx.fillStyle = hexToRGBA(fillColor, fillOpacity);
      ctx.fill();
      ctx.restore();

      // Draw label
      ctx.fillStyle = fontFill;
      ctx.textAlign = "center";
      ctx.fillText(label, labelX, labelY - 5);
    });
  }

  renderSVG(moreProps) {
    const {
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
      fillColor,
      fillOpacity,
      fontFamily,
      fontSize,
      fontFill,
    } = this.props;

    const { arcs } = helper(this.props, moreProps);

    return (
      <g>
        {arcs.map((arc, idx) => {
          const {
            cx,
            cy,
            rx,
            ry,
            startAngle,
            endAngle,
            anticlockwise,
            label,
            labelX,
            labelY,
          } = arc;

          // Convert angles from radians to degrees for SVG
          const startDeg = (startAngle * 180) / Math.PI;
          const endDeg = (endAngle * 180) / Math.PI;
          const angleDiff = endDeg - startDeg;

          // Calculate arc path for SVG
          const x1 = cx + rx * Math.cos(startAngle);
          const y1 = cy + ry * Math.sin(startAngle);
          const x2 = cx + rx * Math.cos(endAngle);
          const y2 = cy + ry * Math.sin(endAngle);

          const largeArcFlag = Math.abs(angleDiff) > 180 ? 1 : 0;
          const sweepFlag = anticlockwise ? 0 : 1;

          const pathD = `M ${x1} ${y1} A ${rx} ${ry} 0 ${largeArcFlag} ${sweepFlag} ${x2} ${y2}`;

          return (
            <g key={idx}>
              {/* Arc path */}
              <path
                d={pathD}
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeOpacity={strokeOpacity}
                fill={fillColor}
                fillOpacity={fillOpacity}
                strokeDasharray={
                  strokeDasharray === "Solid" ? "" : strokeDasharray
                }
              />
              {/* Label */}
              <text
                x={labelX}
                y={labelY - 5}
                fill={fontFill}
                fontSize={fontSize}
                fontFamily={fontFamily}
                textAnchor="middle"
              >
                {label}
              </text>
            </g>
          );
        })}
      </g>
    );
  }

  render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onDragStart, onDrag, onDragComplete, onHover, onUnHover } =
      this.props;

    return (
      <GenericChartComponent
        isHover={this.isHover}
        svgDraw={this.renderSVG}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={["mousemove", "mouseleave", "pan", "drag", "xAxisZoom"]}
      />
    );
  }
}

/**
 * Helper function to calculate Fibonacci Arcs geometry
 * Based on ChartIQ implementation (lines 12650-12695)
 */
function helper(props, moreProps) {
  const { x1, y1, x2, y2, levels } = props;
  const {
    xScale,
    chartConfig: { yScale },
  } = moreProps;

  // Convert to pixel coordinates
  const px1 = xScale(x1);
  const py1 = yScale(y1);
  const px2 = xScale(x2);
  const py2 = yScale(y2);

  // Determine trend direction
  const isUpTrend = py2 < py1;

  // Calculate scale factor for elliptical arcs
  const factor = Math.abs((py2 - py1) / (px2 - px1));

  // Price distance for radius calculation
  const priceDistance = Math.abs(y2 - y1);

  const arcs = levels.map(({ level, label }) => {
    // Calculate radius in price units (ChartIQ line 12650)
    const radius = priceDistance * Math.sqrt(2) * level;

    // Calculate arc position
    const arcValue = y2 + radius * (isUpTrend ? -1 : 1);
    const arcY = yScale(arcValue);

    // Find intersection with trend line for arc center (ChartIQ line 12653)
    // Using similar triangles: (arcY - py1) / (py2 - py1) = (arcX - px1) / (px2 - px1)
    const t = (arcY - py1) / (py2 - py1);
    const arcX = px1 + t * (px2 - px1);

    // Arc parameters
    const rx = Math.abs(arcY - py2) * factor; // Ellipse x-radius (scaled)
    const ry = Math.abs(arcY - py2); // Ellipse y-radius

    // Arc angles (draw semi-circle)
    const startAngle = 0;
    const endAngle = Math.PI;
    const anticlockwise = !isUpTrend;

    return {
      cx: px2,
      cy: py2,
      rx,
      ry,
      startAngle,
      endAngle,
      anticlockwise,
      label,
      labelX: px2,
      labelY: arcY,
    };
  });

  return { arcs };
}

EachFibArc.propTypes = {
  x1: PropTypes.number,
  y1: PropTypes.number,
  x2: PropTypes.number,
  y2: PropTypes.number,

  index: PropTypes.number,
  interactive: PropTypes.bool,
  selected: PropTypes.bool,

  appearance: PropTypes.shape({
    stroke: PropTypes.shape({
      color: PropTypes.string,
      width: PropTypes.number,
      dash: PropTypes.string,
      opacity: PropTypes.number,
    }),
    fill: PropTypes.shape({
      color: PropTypes.string,
      opacity: PropTypes.number,
    }),
    font: PropTypes.shape({
      fontFamily: PropTypes.string,
      fontSize: PropTypes.number,
      color: PropTypes.string,
    }),
    r: PropTypes.number,
  }).isRequired,

  hoverText: PropTypes.object.isRequired,
  yDisplayFormat: PropTypes.func,

  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
  onPoint1Drag: PropTypes.func,
  onPoint2Drag: PropTypes.func,
};

EachFibArc.defaultProps = {
  interactive: true,
  selected: false,
  onDrag: noop,
  onDragComplete: noop,
  onPoint1Drag: noop,
  onPoint2Drag: noop,
  hoverText: {
    enable: false,
  },
};

FibArcComponent.propTypes = {
  x1: PropTypes.number.isRequired,
  y1: PropTypes.number.isRequired,
  x2: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,
  levels: PropTypes.array.isRequired,

  stroke: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  strokeDasharray: PropTypes.string,
  strokeOpacity: PropTypes.number.isRequired,
  fillColor: PropTypes.string.isRequired,
  fillOpacity: PropTypes.number.isRequired,
  fontFamily: PropTypes.string.isRequired,
  fontSize: PropTypes.number.isRequired,
  fontFill: PropTypes.string.isRequired,

  yDisplayFormat: PropTypes.func,
  tolerance: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired,

  interactiveCursorClass: PropTypes.string,
  onDragStart: PropTypes.func.isRequired,
  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
};

FibArcComponent.defaultProps = {
  tolerance: 7,
  selected: false,
  strokeOpacity: 1,
  onDragStart: noop,
  onDrag: noop,
  onDragComplete: noop,
};

export default EachFibArc;
